import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Button } from '../ui/Button';
import { TagBadge } from './TagBadge';

interface Props {
  tags: string[];
  onAdd: (label: string) => void;
  onRemove: (label: string) => void;
  disabled?: boolean;
}

export function TagInput({ tags, onAdd, onRemove, disabled }: Props): JSX.Element {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);

  const existingQuery = useQuery({
    queryKey: ['contacts', 'tags'],
    queryFn: () => api.contacts.tags(),
    staleTime: 60_000,
  });

  const suggestions = (existingQuery.data?.tags ?? []).filter(
    (t) => input !== '' && t.toLowerCase().startsWith(input.toLowerCase()) && !tags.includes(t),
  );

  const handleAdd = (label: string): void => {
    const trimmed = label.trim();
    if (trimmed === '' || tags.includes(trimmed)) return;
    onAdd(trimmed);
    setInput('');
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {tags.map((t) => (
          <TagBadge
            key={t}
            label={t}
            onRemove={disabled === true ? undefined : (): void => onRemove(t)}
          />
        ))}
        <div className="relative flex items-center gap-2">
          <input
            value={input}
            onChange={(e): void => setInput(e.target.value)}
            onFocus={(): void => setFocused(true)}
            onBlur={(): void => {
              setTimeout(() => setFocused(false), 150);
            }}
            onKeyDown={(e): void => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAdd(input);
              }
            }}
            placeholder="Ajouter tag…"
            disabled={disabled === true}
            className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500 w-32"
          />
          <Button
            type="button"
            size="sm"
            onClick={(): void => handleAdd(input)}
            disabled={disabled === true || input.trim() === ''}
          >
            +
          </Button>
          {focused && suggestions.length > 0 && (
            <div className="absolute top-full left-0 mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-lg z-10 w-44 max-h-40 overflow-y-auto">
              {suggestions.slice(0, 8).map((s) => (
                <button
                  key={s}
                  type="button"
                  onMouseDown={(): void => handleAdd(s)}
                  className="block w-full text-left px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
